import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  DollarSign, Plus, Loader2, Trash2, Pencil, Youtube, TrendingUp, CheckCircle2, AlertTriangle, Link as LinkIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const inputCls = "bg-slate-900 border-slate-800 text-slate-100 focus-visible:ring-emerald-500/30";
const SOURCES = ["adsense-payout", "sponsorship", "affiliate", "other"];
const SOURCE_META = [
  { key: "sponsorship", label: "Sponsorship" },
  { key: "affiliate", label: "Affiliate" },
  { key: "adsense-payout", label: "AdSense (manual)" },
  { key: "other", label: "Other" },
];
const subtotalText = (byCur) => {
  const entries = Object.entries(byCur || {});
  if (!entries.length) return "0.00";
  return entries.map(([c, v]) => `${c} ${fmt(v)}`).join(" · ");
};
const today = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => {
  const v = Number(n || 0);
  // Honesty: a real cost >0 but under a cent must never render as "0.00".
  if (v !== 0 && Math.abs(v) < 0.01) return v > 0 ? "<0.01" : "-<0.01";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function IncomeDialog({ channelId, projects, initial, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(initial || { amount: "", currency: "USD", date: today(), source_type: "sponsorship", project_id: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!channelId && !initial) { toast.error("Select a channel first."); return; }
    if (!f.amount || Number(f.amount) <= 0) { toast.error("Amount must be greater than 0."); return; }
    setBusy(true);
    try {
      const body = { channel_id: initial?.channel_id || channelId, amount: Number(f.amount), currency: f.currency.toUpperCase(),
        date: f.date, source_type: f.source_type, project_id: f.project_id || null, note: f.note };
      if (initial?.id) await api.put(`/earnings/income/${initial.id}`, body);
      else await api.post("/earnings/income", body);
      toast.success(initial?.id ? "Income updated." : `Income logged: ${body.currency} ${fmt(body.amount)}.`);
      setOpen(false); onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {initial?.id
          ? <Button size="sm" variant="ghost" className="text-slate-300 hover:bg-slate-800" data-testid={`income-edit-${initial.id}`}><Pencil className="w-4 h-4" /></Button>
          : <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="income-add"><Plus className="w-4 h-4 mr-1.5" /> Log income</Button>}
      </DialogTrigger>
      <DialogContent className="bg-[#111827] border-slate-800 text-slate-100">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit income" : "Log income"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300 text-sm mb-1 block">Amount</Label><Input type="number" className={inputCls} value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="income-amount" /></div>
            <div><Label className="text-slate-300 text-sm mb-1 block">Currency</Label><Input className={inputCls} value={f.currency} onChange={(e) => set("currency", e.target.value)} maxLength={3} data-testid="income-currency" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-slate-300 text-sm mb-1 block">Date</Label><Input type="date" className={inputCls} value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="income-date" /></div>
            <div><Label className="text-slate-300 text-sm mb-1 block">Source</Label>
              <Select value={f.source_type} onValueChange={(v) => set("source_type", v)}>
                <SelectTrigger className={inputCls} data-testid="income-source"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-slate-700 text-slate-200">
                  {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-slate-300 text-sm mb-1 block">Link to video <span className="text-slate-600">(optional)</span></Label>
            <Select value={f.project_id || "none"} onValueChange={(v) => set("project_id", v === "none" ? "" : v)}>
              <SelectTrigger className={inputCls} data-testid="income-project"><SelectValue placeholder="No video" /></SelectTrigger>
              <SelectContent className="bg-[#111827] border-slate-700 text-slate-200 max-h-60">
                <SelectItem value="none">No video</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-slate-300 text-sm mb-1 block">Note</Label><Input className={inputCls} value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="income-note" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="income-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IncomeTable({ rows, channelId, projects, load, del }) {
  if (!rows.length)
    return <div className="py-6"><EmptyState icon={DollarSign} title="No entries in this section" description="Log income and pick this source type to see it here." /></div>;
  return (
    <table className="w-full text-sm" data-testid="income-list">
      <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-800">
        <th className="px-5 py-2.5">Date</th><th className="px-5 py-2.5">Amount</th><th className="px-5 py-2.5">Video</th><th className="px-5 py-2.5">Note</th><th className="px-5 py-2.5"></th>
      </tr></thead>
      <tbody>
        {rows.map((i) => (
          <tr key={i.id} className="border-b border-slate-800/50 text-slate-300" data-testid={`income-row-${i.id}`}>
            <td className="px-5 py-3">{i.date}</td>
            <td className="px-5 py-3 text-emerald-400">{i.currency} {fmt(i.amount)}</td>
            <td className="px-5 py-3 text-slate-500">{i.project_id ? <span className="inline-flex items-center gap-1"><LinkIcon className="w-3 h-3" /> linked</span> : "—"}</td>
            <td className="px-5 py-3 text-slate-500 max-w-[180px] truncate">{i.note || "—"}</td>
            <td className="px-5 py-3 flex items-center gap-1">
              <IncomeDialog channelId={channelId} projects={projects} initial={i} onDone={load} />
              <Button size="sm" variant="ghost" className="text-rose-400 hover:bg-slate-800" onClick={() => del(i.id)} data-testid={`income-del-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-4">
      <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-2xl font-display font-bold mt-1 ${accent || "text-slate-100"}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-600 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function Earnings() {
  const { channels, selectedChannelId } = useStudio();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [yt, setYt] = useState(null);
  const [income, setIncome] = useState([]);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const cq = selectedChannelId ? `?channel_id=${selectedChannelId}` : "";
      const [s, t, y, inc, pj] = await Promise.all([
        api.get(`/earnings/summary${cq}`), api.get(`/earnings/trend${cq}`),
        api.get(`/earnings/youtube-status${cq}`), api.get(`/earnings/income${cq}`),
        api.get(`/projects${cq}`),
      ]);
      setSummary(s.data); setTrend(t.data); setYt(y.data); setIncome(inc.data);
      const flat = Object.values(pj.data.grouped || {}).flat();
      setProjects(flat.map((p) => ({ id: p.id, title: (p.metadata?.title) || p.title || "Untitled" })));
    } catch (e) { setError(apiError(e)); }
  }, [selectedChannelId]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { try { await api.delete(`/earnings/income/${id}`); toast.success("Deleted."); load(); } catch (e) { toast.error(apiError(e)); } };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!summary) return <LoadingState label="Loading earnings…" />;

  const cur = summary.currency;
  const maxBar = Math.max(1, ...(trend?.months || []).map((m) => Math.max(m.income, m.cost)));
  const channelName = channels.find((c) => c.id === selectedChannelId)?.data?.name || "All channels";

  return (
    <div className="animate-in-up">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <p className="text-[11px] font-semibold tracking-widest uppercase text-emerald-400 mb-1 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Money</p>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-50">Earnings</h1>
          <p className="text-sm text-slate-400 mt-2">Manual income vs real production costs. Scope: <span className="text-slate-200">{channelName}</span>. Revenue is only what you enter or an API returns — never invented.</p>
        </div>
        <IncomeDialog channelId={selectedChannelId} projects={projects} onDone={load} />
      </div>

      {/* Net summary */}
      {summary.mixed_currency ? (
        <div className="bg-[#111827] border border-amber-500/30 rounded-xl p-4 mb-5" data-testid="earnings-mixed">
          <p className="text-sm text-amber-300 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Mixed currencies — shown as honest per-currency subtotals (no conversion).</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {summary.per_currency.map((p) => (
              <div key={p.currency} className="bg-slate-900/50 border border-slate-800 rounded-lg p-3" data-testid={`earnings-cur-${p.currency}`}>
                <p className="text-xs text-slate-500">{p.currency}</p>
                <p className="text-sm text-slate-200">income {fmt(p.income)} · cost {fmt(p.cost)}</p>
                <p className={`text-lg font-bold ${p.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>net {fmt(p.net)}</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5" data-testid="earnings-summary">
          <Stat label="Income" value={`${cur} ${fmt(summary.income_total)}`} accent="text-emerald-400" sub={`${summary.entries_count} entr${summary.entries_count === 1 ? "y" : "ies"}`} />
          <Stat label="Production cost" value={`${cur} ${fmt(summary.costs_total)}`} accent="text-slate-300" sub="from cost ledger" />
          <Stat label="Net" value={`${cur} ${fmt(summary.net)}`} accent={summary.net >= 0 ? "text-emerald-400" : "text-rose-400"} sub="income − production cost" />
        </div>
      )}

      {/* Trend */}
      <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 mb-5">
        <h3 className="text-sm font-semibold text-slate-100 mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-300" /> Last 6 months ({trend.currency})</h3>
        <div className="flex items-end gap-4 h-40" data-testid="earnings-trend">
          {trend.months.map((m) => (
            <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end justify-center gap-1 h-32">
                <div className="w-1/3 bg-emerald-500/70 rounded-t" style={{ height: `${(m.income / maxBar) * 100}%` }} title={`income ${fmt(m.income)}`} />
                <div className="w-1/3 bg-slate-600 rounded-t" style={{ height: `${(m.cost / maxBar) * 100}%` }} title={`cost ${fmt(m.cost)}`} />
              </div>
              <span className="text-[10px] text-slate-500">{m.month.slice(5)}</span>
              {m.empty ? <span className="text-[9px] text-slate-700">no entries</span> : <span className={`text-[10px] ${m.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmt(m.net)}</span>}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500/70 rounded-sm" /> income</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-slate-600 rounded-sm" /> production cost</span>
        </div>
      </div>

      {/* YouTube revenue — honest state */}
      <div className="bg-[#111827] border border-slate-800/80 rounded-xl p-5 mb-5" data-testid="earnings-youtube">
        <h3 className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Youtube className="w-4 h-4 text-rose-400" /> YouTube revenue</h3>
        {yt.connected && yt.monetary_scope ? (
          <p className="text-xs text-slate-400">{yt.reason}</p>
        ) : (
          <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
            <p className="text-sm text-amber-300 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {yt.reason}</p>
            <ol className="mt-2 space-y-1 text-xs text-slate-400 list-decimal list-inside">
              {(yt.setup_steps || []).map((s) => <li key={s}>{s}</li>)}
            </ol>
            <Button size="sm" onClick={() => navigate("/integrations")} className="mt-3 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700" data-testid="earnings-connect-yt">Go to Integrations</Button>
          </div>
        )}
      </div>

      {/* Per-video profitability */}
      <div className="bg-[#111827] border border-slate-800/80 rounded-xl overflow-hidden mb-5">
        <div className="px-5 py-3 border-b border-slate-800 text-sm font-semibold text-slate-200">Per-video profitability</div>
        {summary.per_video.length === 0 ? (
          <div className="p-8"><EmptyState title="No video costs or income yet" description="Costs appear as you produce; link income to a video to see profitability." /></div>
        ) : (
          <table className="w-full text-sm" data-testid="earnings-pervideo">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-800">
              <th className="px-5 py-2.5">Video</th><th className="px-5 py-2.5">Cost</th><th className="px-5 py-2.5">Assigned income</th><th className="px-5 py-2.5">Net</th><th className="px-5 py-2.5"></th>
            </tr></thead>
            <tbody>
              {summary.per_video.map((v) => (
                <tr key={v.project_id} className="border-b border-slate-800/50 text-slate-300" data-testid={`pervideo-${v.project_id}`}>
                  <td className="px-5 py-3 max-w-xs truncate">{v.title}</td>
                  <td className="px-5 py-3">{v.currency} {fmt(v.cost)}</td>
                  <td className="px-5 py-3">{v.currency} {fmt(v.assigned_income)}</td>
                  <td className={`px-5 py-3 ${v.net >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{v.currency} {fmt(v.net)}</td>
                  <td className="px-5 py-3">
                    {v.paid_for_itself
                      ? <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400" data-testid={`paid-${v.project_id}`}><CheckCircle2 className="w-3 h-3" /> paid for itself</span>
                      : v.currency_mismatch
                        ? <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400" data-testid={`mismatch-${v.project_id}`} title="Income and cost are in different currencies — no conversion, so we can't judge profitability."><AlertTriangle className="w-3 h-3" /> currency mismatch</span>
                        : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Income entries — separate source-type sections */}
      <div className="bg-[#111827] border border-slate-800/80 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800 text-sm font-semibold text-slate-200">Income by source</div>
        <div className="p-4">
          <Tabs defaultValue="sponsorship">
            <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto">
              {SOURCE_META.map((s) => (
                <TabsTrigger key={s.key} value={s.key} data-testid={`income-tab-${s.key}`}
                  className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white text-slate-300">
                  {s.label}
                  <span className="ml-1.5 text-xs opacity-70">{summary.source_counts?.[s.key] || 0}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            {SOURCE_META.map((s) => {
              const rows = income.filter((i) => (SOURCES.includes(i.source_type) ? i.source_type : "other") === s.key);
              return (
                <TabsContent key={s.key} value={s.key} data-testid={`income-section-${s.key}`}>
                  <div className="flex items-center justify-between px-1 py-3 text-sm border-b border-slate-800/60">
                    <span className="text-slate-400">{rows.length} entr{rows.length === 1 ? "y" : "ies"}</span>
                    <span className="text-slate-200">Subtotal: <span className="text-emerald-400 font-medium" data-testid={`income-subtotal-${s.key}`}>{subtotalText(summary.income_by_source?.[s.key])}</span></span>
                  </div>
                  <IncomeTable rows={rows} channelId={selectedChannelId} projects={projects} load={load} del={del} />
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </div>
    </div>
  );
}
